import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Play, ChevronLeft, ChevronRight, Grid3X3, List } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { useUserShows } from '../hooks/useUserShows';
import { PlatformType } from '../types/Show';
import { getShowThumbnailUrl } from '../utils/thumbnailUtils';
import styles from '../styles/App.module.css';

export function AllShowsScreen() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTenant, setSelectedTenant] = useState<PlatformType>(PlatformType.DASHREEL);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('table');
  const PAGE_SIZE = 20;
  const SEARCH_DEBOUNCE_MS = 300;

  // Calculate offset for current page
  const offset = (currentPage - 1) * PAGE_SIZE;

  // The search runs server-side, so wait for typing to settle before issuing a request.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const {
    shows,
    loading: showsLoading,
    error: showsError,
    hasNext
  } = useUserShows(PAGE_SIZE, offset, selectedTenant, debouncedSearchTerm);

  const handleCreateNew = () => {
    navigate('/publish');
  };

  const handleShowClick = (showId: string) => {
    console.log('Show clicked:', showId);
    if (showId) {
      navigate(`/publish/${showId}`);
    } else {
      console.error('No showId provided');
      navigate('/publish');
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleTenantChange = (value: PlatformType) => {
    setSelectedTenant(value);
    setCurrentPage(1);
  };

  const splitPills = (value?: string | null) =>
    value
      ? value.split(',').map(item => item.trim()).filter(Boolean)
      : [];

  const renderPills = (items: string[]) => {
    if (items.length === 0) {
      return <span className="text-xs text-muted-foreground bg-muted">-</span>;
    }

    return (
      <div className="flex flex-wrap gap-1.5">
        {items.map(item => (
          <span
            key={item}
            className="rounded border border-border bg-secondary/40 px-2 py-0.5 text-xs text-foreground"
          >
            {item}
          </span>
        ))}
      </div>
    );
  };

  // Pagination handlers
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (hasNext) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Check if we have previous page
  const hasPrevious = currentPage > 1;

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.contentWrapper}>
        {/* Navigation */}
        <div className={styles.navigation}>
          <div className={styles.navContent}>
            <h1 className={styles.navTitle}>All Shows</h1>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleCreateNew}
                className={styles.navButton}
              >
                <Plus className="h-4 w-4 mr-2" />
                Create New
              </Button>
            </div>
          </div>
        </div>

        {/* Search and Shows Section */}
        <div className="px-4 mt-8">
          {/* Search Bar */}
          <div className="mb-8 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="relative w-full lg:max-w-md">
              <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <Input
                placeholder="Search your shows by name..."
                className="pl-12"
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center lg:justify-end">
              <Select value={selectedTenant} onValueChange={handleTenantChange}>
                <SelectTrigger className="sm:w-48">
                  <SelectValue placeholder="Select tenant" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={PlatformType.DASHREEL}>DashReels</SelectItem>
                  <SelectItem value={PlatformType.SHORTFREE}>ShortFree</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex h-10 rounded-md border bg-background p-1">
                <Button
                  type="button"
                  variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('grid')}
                  className="h-8 px-3"
                  aria-pressed={viewMode === 'grid'}
                >
                  <Grid3X3 className="h-4 w-4 mr-2" />
                  Grid
                </Button>
                <Button
                  type="button"
                  variant={viewMode === 'table' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('table')}
                  className="h-8 px-3"
                  aria-pressed={viewMode === 'table'}
                >
                  <List className="h-4 w-4 mr-2" />
                  Table
                </Button>
              </div>
            </div>
          </div>
        {/* Loading Skeleton */}
        {showsLoading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
            {Array.from({ length: 8 }).map((_, index) => (
              <div key={`skeleton-${index}`} className={styles.projectCard}>
                <Skeleton className="w-full aspect-square rounded-lg" />
                <div className="pt-2">
                  <Skeleton className="h-4 w-3/4 mb-2" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Error State */}
        {!showsLoading && showsError && (
          <div className="flex flex-col items-center justify-center text-center py-16">
            <Play className="h-16 w-16 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">Failed to load shows</h3>
            <p className="text-muted-foreground mb-4">Please try refreshing the page.</p>
            <Button variant="outline" onClick={() => window.location.reload()}>
              Refresh
            </Button>
          </div>
        )}

        {/* Empty State */}
        {!showsLoading && !showsError && !debouncedSearchTerm && shows.length === 0 && (
          <div className="flex flex-col items-center justify-center text-center py-16">
            <Play className="h-16 w-16 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No shows yet</h3>
            <p className="text-muted-foreground mb-4">Create your first show to get started.</p>
            <Button onClick={handleCreateNew}>
              <Plus className="h-4 w-4 mr-2" />
              Create Your First Show
            </Button>
          </div>
        )}

        {/* No Search Results */}
        {!showsLoading && !showsError && debouncedSearchTerm && shows.length === 0 && (
          <div className="flex flex-col items-center justify-center text-center py-16">
            <Search className="h-16 w-16 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No shows found</h3>
            <p className="text-muted-foreground mb-4">
              No shows match "{debouncedSearchTerm}"
            </p>
            <Button variant="outline" onClick={() => handleSearchChange('')}>
              Clear Search
            </Button>
          </div>
        )}

        {/* Shows Grid */}
        {!showsLoading && !showsError && shows.length > 0 && viewMode === 'grid' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
            {shows.map((show) => (
              <div
                key={show.id}
                className={styles.projectCard}
                onClick={() => handleShowClick(show.id)}
                style={{ cursor: 'pointer' }}
              >
                <Card className="overflow-hidden border-0 shadow-sm hover:shadow-md transition-shadow bg-card">
                  <CardContent className="p-0">
                    <img 
                      src={getShowThumbnailUrl(show)} 
                      alt={show.name} 
                      className={styles.projectImage}
                    />
                  </CardContent>
                </Card>
                <div className={styles.projectInfo}>
                  <h3 className={styles.projectTitle}>{show.name}</h3>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Shows Table */}
        {!showsLoading && !showsError && shows.length > 0 && viewMode === 'table' && (
          <div className="rounded-lg border bg-card overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[84px]"></TableHead>
                  <TableHead>Show Name</TableHead>
                  <TableHead>Genre</TableHead>
                  <TableHead>Tropes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {shows.map((show) => (
                  <TableRow
                    key={show.id}
                    onClick={() => handleShowClick(show.id)}
                    className="cursor-pointer"
                  >
                    <TableCell>
                      <img
                        src={getShowThumbnailUrl(show)}
                        alt={show.name}
                        className="h-14 w-10 rounded border object-cover bg-secondary"
                        draggable={false}
                      />
                    </TableCell>
                    <TableCell className="min-w-[220px]">
                      <div className="font-medium text-foreground">{show.name}</div>
                    </TableCell>
                    <TableCell className="max-w-[280px]">
                      {renderPills(splitPills(show.genre))}
                    </TableCell>
                    <TableCell className="max-w-[360px]">
                      {renderPills(splitPills(show.trope))}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Pagination Controls - search results are paginated server-side too */}
        {!showsLoading && !showsError && shows.length > 0 && (hasPrevious || hasNext) && (
          <div className="flex justify-between items-center mt-8 p-4 bg-card rounded-lg border">
            <div className="text-sm text-muted-foreground">
              Page {currentPage} - Showing {shows.length} shows
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePreviousPage}
                disabled={!hasPrevious}
                className="h-8 px-3"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={handleNextPage}
                disabled={!hasNext}
                className="h-8 px-3"
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}
